import { getRoutePointColor } from '@/utils/routePointGrouping'
import { Z_INDEX } from '@/utils/zIndex'

/**
 * Reusable DOM-marker factories used by `RoutePointLayer`, which is the
 * single source of truth for route-point markers (persisted + pending) and
 * the routing "spinner". Keeping the factories in one place guarantees the
 * workspace routing flow keeps a consistent look & feel: numbered circles for
 * persisted RPs (start "A" green, end "B" red, intermediates blue — shared with
 * the route-point list via `getRoutePointColor`), amber dashed markers for
 * pending RPs, and the amber dashed routing-spinner overlay during BRouter calculations.
 */

/**
 * Solid numbered route-point marker (28px circle, 48px touch area). The fill is
 * derived from the label so start (A) / end (B) / intermediate points read
 * distinctly — identical to the list. Returns both the container and the touch
 * area so callers can attach click/touch/drag handlers to the wide hit-target.
 */
export const createRoutePointMarkerElement = (
  label: string,
): {
  element: HTMLDivElement
  touchArea: HTMLDivElement
  setHovered: (hovered: boolean) => void
} => {
  const container = document.createElement('div')
  container.className = 'route-point-marker-container relative'
  container.style.zIndex = Z_INDEX.MAP_MARKERS_ROUTE_POINT.toString()

  const touchArea = document.createElement('div')
  touchArea.style.position = 'absolute'
  touchArea.style.width = '48px'
  touchArea.style.height = '48px'
  touchArea.style.top = '-24px'
  touchArea.style.left = '-24px'
  touchArea.style.borderRadius = '50%'
  touchArea.style.cursor = 'grab'
  touchArea.style.background = 'transparent'
  touchArea.style.touchAction = 'none'

  const markerEl = document.createElement('div')
  markerEl.className = 'route-point-marker'
  markerEl.style.position = 'absolute'
  markerEl.style.top = '-14px'
  markerEl.style.left = '-14px'
  markerEl.style.width = '28px'
  markerEl.style.height = '28px'
  markerEl.style.borderRadius = '50%'
  markerEl.style.backgroundColor = getRoutePointColor(label)
  markerEl.style.border = '2px solid white'
  markerEl.style.transition = 'border-color 120ms ease'
  markerEl.style.display = 'flex'
  markerEl.style.alignItems = 'center'
  markerEl.style.justifyContent = 'center'
  markerEl.style.pointerEvents = 'none'
  markerEl.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)'

  const numberEl = document.createElement('span')
  numberEl.textContent = label
  numberEl.style.color = 'white'
  numberEl.style.fontSize = '12px'
  numberEl.style.fontWeight = '700'
  numberEl.style.lineHeight = '1'

  markerEl.appendChild(numberEl)
  container.appendChild(touchArea)
  container.appendChild(markerEl)

  return {
    element: container,
    touchArea,
    setHovered: (hovered: boolean) => {
      // Neutral dark ring on hover reads against any fill (green/red/blue).
      markerEl.style.borderColor = hovered ? '#1F2937' : 'white'
    },
  }
}

/**
 * Amber dashed-border numbered marker, used for "pending" route points that
 * will be persisted once the user confirms.
 */
export const createPendingRoutePointMarkerElement = (
  label: string,
): {
  element: HTMLDivElement
  touchArea: HTMLDivElement
  setHovered: (hovered: boolean) => void
} => {
  const container = document.createElement('div')
  container.className = 'route-point-marker-container relative'
  container.style.zIndex = Z_INDEX.MAP_MARKERS_ROUTE_POINT.toString()

  const touchArea = document.createElement('div')
  touchArea.style.position = 'absolute'
  touchArea.style.width = '48px'
  touchArea.style.height = '48px'
  touchArea.style.top = '-24px'
  touchArea.style.left = '-24px'
  touchArea.style.borderRadius = '50%'
  touchArea.style.cursor = 'grab'
  touchArea.style.background = 'transparent'
  touchArea.style.touchAction = 'none'

  const markerEl = document.createElement('div')
  markerEl.className = 'route-point-marker route-point-marker--pending'
  markerEl.style.position = 'absolute'
  markerEl.style.top = '-14px'
  markerEl.style.left = '-14px'
  markerEl.style.width = '28px'
  markerEl.style.height = '28px'
  markerEl.style.borderRadius = '50%'
  markerEl.style.backgroundColor = '#F59E0B'
  markerEl.style.border = '2px dashed white'
  markerEl.style.transition = 'border-color 120ms ease'
  markerEl.style.display = 'flex'
  markerEl.style.alignItems = 'center'
  markerEl.style.justifyContent = 'center'
  markerEl.style.pointerEvents = 'none'
  markerEl.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)'

  const labelEl = document.createElement('div')
  labelEl.style.position = 'absolute'
  labelEl.style.top = '-14px'
  labelEl.style.left = '-14px'
  labelEl.style.width = '28px'
  labelEl.style.height = '28px'
  labelEl.style.display = 'flex'
  labelEl.style.alignItems = 'center'
  labelEl.style.justifyContent = 'center'
  labelEl.style.pointerEvents = 'none'

  const numberEl = document.createElement('span')
  numberEl.textContent = label
  numberEl.style.color = 'white'
  numberEl.style.fontSize = '12px'
  numberEl.style.fontWeight = '700'
  numberEl.style.lineHeight = '1'

  labelEl.appendChild(numberEl)
  container.appendChild(touchArea)
  container.appendChild(markerEl)
  container.appendChild(labelEl)

  return {
    element: container,
    touchArea,
    setHovered: (hovered: boolean) => {
      markerEl.style.borderColor = hovered ? '#2563EB' : 'white'
    },
  }
}

/**
 * Purple outlined marker used for sparse-track preview helper points.
 * These markers are visual-only and are not persisted until the user confirms.
 */
export const createPreviewRoutePointMarkerElement = (
  label: string,
): {
  element: HTMLDivElement
  touchArea: HTMLDivElement
  setHovered: (hovered: boolean) => void
} => {
  const container = document.createElement('div')
  container.className = 'route-point-marker-container relative'
  container.style.zIndex = Z_INDEX.MAP_MARKERS_ROUTE_POINT.toString()

  const touchArea = document.createElement('div')
  touchArea.style.position = 'absolute'
  touchArea.style.width = '40px'
  touchArea.style.height = '40px'
  touchArea.style.top = '-20px'
  touchArea.style.left = '-20px'
  touchArea.style.borderRadius = '50%'
  touchArea.style.pointerEvents = 'none'

  const markerEl = document.createElement('div')
  markerEl.className = 'route-point-marker route-point-marker--preview'
  markerEl.style.position = 'absolute'
  markerEl.style.top = '-12px'
  markerEl.style.left = '-12px'
  markerEl.style.width = '24px'
  markerEl.style.height = '24px'
  markerEl.style.borderRadius = '50%'
  markerEl.style.backgroundColor = '#ffffff'
  markerEl.style.border = '2px solid #8B5CF6'
  markerEl.style.display = 'flex'
  markerEl.style.alignItems = 'center'
  markerEl.style.justifyContent = 'center'
  markerEl.style.pointerEvents = 'none'
  markerEl.style.boxShadow = '0 1px 3px rgba(0,0,0,0.25)'

  const numberEl = document.createElement('span')
  numberEl.textContent = label
  numberEl.style.color = '#7C3AED'
  numberEl.style.fontSize = '11px'
  numberEl.style.fontWeight = '700'
  numberEl.style.lineHeight = '1'

  markerEl.appendChild(numberEl)
  container.appendChild(touchArea)
  container.appendChild(markerEl)

  return {
    element: container,
    touchArea,
    setHovered: () => undefined,
  }
}

/**
 * Amber dashed rotating spinner ring shown on the route points affected by the
 * current BRouter calculation. Uses the global
 * `.route-point-spinning-marker` CSS animation defined in RoutePointLayer.vue.
 */
export const createSpinningRoutePointMarkerElement = (): HTMLDivElement => {
  const container = document.createElement('div')
  container.className = 'route-point-marker-container relative'
  container.style.zIndex = Z_INDEX.MAP_MARKERS_ROUTE_POINT.toString()

  const markerEl = document.createElement('div')
  markerEl.className = 'route-point-spinning-marker'
  markerEl.style.position = 'absolute'
  markerEl.style.top = '-16px'
  markerEl.style.left = '-16px'
  markerEl.style.width = '32px'
  markerEl.style.height = '32px'
  markerEl.style.borderRadius = '50%'
  markerEl.style.backgroundColor = 'transparent'
  markerEl.style.border = '3px dashed #F59E0B'
  markerEl.style.boxSizing = 'border-box'
  markerEl.style.pointerEvents = 'none'
  markerEl.style.boxShadow = '0 0 0 1px rgba(255,255,255,0.95)'

  container.appendChild(markerEl)
  return container
}
